/**
 * Session Polling Hook
 *
 * Polls the active session endpoint every N seconds to get real-time updates
 * on session counters (total_pdfs, pdfs_processed_count, etc.)
 *
 * Alternative to Server-Sent Events (SSE) that works without backend changes.
 * Can be upgraded to SSE when backend implements streaming endpoint.
 */

import { useEffect, useRef } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { sessionsApi } from '../api/sessions';

interface UseSessionPollingOptions {
  /**
   * Polling interval in milliseconds
   * @default 5000 (5 seconds)
   */
  interval?: number;

  /**
   * Whether to enable polling
   * @default true
   */
  enabled?: boolean;
}

export const useSessionPolling = (options: UseSessionPollingOptions = {}) => {
  const { interval = 5000, enabled = true } = options;

  const { activeSession, customerId, setActiveSession } = useSessionStore();
  const intervalRef = useRef<number | null>(null);
  // Use refs for values that change on every poll to avoid re-triggering the effect
  const sessionIdRef = useRef<string | null>(null);
  const customerIdRef = useRef<string>(customerId);
  const setActiveSessionRef = useRef(setActiveSession);

  // Keep refs in sync without re-running the effect
  sessionIdRef.current = activeSession?.session_id ?? null;
  customerIdRef.current = customerId;
  setActiveSessionRef.current = setActiveSession;

  useEffect(() => {
    // Don't poll if disabled or no active session
    if (!enabled || !sessionIdRef.current) {
      return;
    }

    const pollSession = async () => {
      const sid = sessionIdRef.current;
      const cid = customerIdRef.current;
      if (!sid) return;

      try {
        const updatedSession = await sessionsApi.getSession(sid, cid);
        setActiveSessionRef.current(updatedSession);
      } catch (error) {
        console.error('Failed to poll session:', error);
      }
    };

    // Poll immediately on mount
    pollSession();

    // Set up interval for subsequent polls
    intervalRef.current = setInterval(pollSession, interval) as unknown as number;

    // Cleanup on unmount or when polling is disabled
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // Only re-run when polling is toggled or interval changes — NOT on every session update
  }, [enabled, interval, !!activeSession]);

  return {
    isPolling: enabled && !!activeSession,
  };
};
